// src/com/sms_message/SmsMessage.js
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import BackupIcon from '@mui/icons-material/Backup';
import SmsIcon from '@mui/icons-material/Sms';
import { AppNumber, AppObject, AppPalette } from '../../model/AppConst';
import { useLazyQuery, useMutation, useReactiveVar } from '@apollo/client';
import { smsMessageReloadSignalRepo, smsPhraseListRepo, userInfoListRepo, userInfoRepo } from '../../model/CvoModel';
import { AppBar, Button, IconButton, MenuItem, Select, Tab, Tabs, TextField, Tooltip } from '@mui/material';
import { GET_CUST_INFO } from '../cust_info/CustInfoGql';
import ResponseAlert from '../message/ResponseAlert';
import ValueUtil from '../../model/ValueUtil';
import SmsPhrase from '../sms_phrase/SmsPhrase';
import { ADD_SMS_PHRASE, GET_SMS_PHRASE_LIST } from '../sms_phrase/SmsPhraseGql';
import VehInfoListForSms from '../veh_info/VehInfoListForSms';
import AddressInfoListForSms from '../address_info/AddressInfoListForSms';
import UserInfoListForSms from '../user_info/UserInfoListForSms';
import { ADD_SMS_MESSAGE } from './SmsMessageGql';
import { useSnackbar } from 'notistack';


const dcPad = AppNumber.dataContainerPadding;
const SmsMessageContainer = styled(Box)({
    position:"absolute", top:dcPad, bottom:dcPad, left:dcPad, right:dcPad, display:"flex"
});
const InputBox = styled(Box)({
    width: 480, border: '1px solid #cde', borderRadius: 25, marginRight: 10, // padding: 10,
    display: 'flex', flexDirection: 'column',
});

const ListsContainer = styled(Box)({
    flexGrow: 1, display: 'flex', flexDirection: 'column',
});
// ----- InputBox -------
const InputHeaderBox = styled(Box)({
    backgroundColor: '#def', padding: 20, borderTopLeftRadius: 25, borderTopRightRadius: 25,
    display: 'flex', alignItems: 'center'
});

const ReceiverListBox = styled(Box)({ flexGrow: 1, overflow: 'auto' });
const ReceiverList = styled(Box)({ padding: '10px 0' });
const ReceiverItem = styled(Box)({
    borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center',
    '&:hover': {backgroundColor: '#eee'}
});
const ReceiverNumber = styled(Box)({width: 40, textAlign:'center'});
const ReceiverTel = styled(Box)({width: 120, padding: 10});
const ReceiverName = styled(Box)({flexGrow: 1, padding: 10});
const ReceiverControl = styled(Box)({paddingRight: 10});
const AnonSenderBox = styled(Box)({ padding: 10, backgroundColor: '#def', display: 'flex' });
const SenderBox = styled(Box)({ padding: 10, display: 'flex' });
const TextBox = styled(Box)({ padding: 10, backgroundColor: '#def', borderBottomLeftRadius: 25, borderBottomRightRadius: 25 });

const HelperList = {
    UserList: 1, VehicleList: 2, AddressList: 3, SmsPhrases: 4
}

function hasTel(list, tel) {
    return list.some(item => item.tel === tel);
}

function makeSenderList(custInfo, userList) {
    const senders = [];
    if(custInfo) {
        senders.push({name: custInfo.custName, tel: custInfo.telNo});
    }
    if(userList) {
        userList
        .filter(u=>{
            const mobile = u.mobile ? u.mobile.replace(/[^0-9]/g, '') : '';
            return ValueUtil.isMobileNumber(mobile);
        })
        .forEach(user => {
            senders.push({name: `${user.userName}, ${user.className}`, tel: user.mobile});
        });
    }
    return senders;
}

/**
 * SMS 메시지를 보내는 컴포넌트.
 * 좌측: 수신자 목록 + 메시지 입력
 * 우측: 탭(차량, 사용자, 주소록, 자주 쓰는 문구)
 * @returns 
 */
export default function SmsMessage() {
    const [message, setMessage] = useState('');
    const [messageChanged, setMessageChanged] = useState(false);
    const [receivers, setReceivers] = useState([]); // {tel,name}
    const [telAnon, setTelAnon] = useState('');
    const [hoveredReceiverIndex, setHoveredReceiverIndex] = useState(null);
    const [senderList, setSenderList] = useState([]); // {tel, name}
    const [callback, setCallback] = useState(null); // tel of senderList item
    const [listNow, setListNow] = useState(HelperList.VehicleList);
    const [custInfo, setCustInfo] = useState(null);
    const userInfoList = useReactiveVar(userInfoListRepo);
    const smsMessageReloadSignal = useReactiveVar(smsMessageReloadSignalRepo);
    const sessionInfo = useReactiveVar(userInfoRepo);
    const { enqueueSnackbar } = useSnackbar();
    const [responseAlert, setResponseAlert] = useState(null);
    const [getCustInfoItem, responseCustItem] = useLazyQuery(GET_CUST_INFO, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetCustInfoItem(data, option)},
        onError: (error) => {setResponseAlert({open:true, error: error, title: "회사정보 조회 오류"})}
    });
    const [addSmsPhrase, responseAddPhrase] = useMutation( ADD_SMS_PHRASE, {
            onCompleted: (data, option) => onCompleteAddPhrase(data, option),
            onError: (error) => {setResponseAlert({open:true, error: error, title: "자주 쓰는 문구 추가 오류"})}
        }
    );
    const [getSmsPhraseList, responsePhraseList] = useLazyQuery(GET_SMS_PHRASE_LIST, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetPhraseList(data, option)},
        onError: (error) => {setResponseAlert({open: true, error: error, title: 'SMS 전송기록 조회 오류'})}
    });
    const [sendSmsMessage, responseSend] = useMutation( ADD_SMS_MESSAGE, {
        onCompleted: (data, option) => onCompleteSendSms(data, option),
        onError: (error) => {setResponseAlert({open:true, error: error, title: "SMS 전송 오류"})}
    });
    /* const [getSmsMessageList, responseSmsMessageList] = useLazyQuery(LIST_SMS_MESSAGE, {
        ...AppObject.NoCachedFetch,
        onCompleted: (data, option) => {onCompleteGetSmsList(data, option)},
        onError: (error) => {setResponseAlert({open: true, error: error, title: 'SMS 전송기록 조회 오류'})}
    }); */

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    /* const onCompleteGetSmsList = (data, clientOption) => {
        if(data.smsMessageList) {
            smsMessageListRepo(data.smsMessageList);
        }
    }; */

    useEffect(() => {
        getCustInfoItem();
    }, []);

    useEffect(()=>{
        const newList = makeSenderList(custInfo, userInfoList.filter(u=>u.userId === sessionInfo.user.userId));
        setSenderList(newList);
        setCallback(newList.length>0 ? newList[0].tel : null);
    }, [custInfo, userInfoList, sessionInfo]);

    // >>>>>>>>> callbacks <<<<<<<<<<<<<
    const onCompleteGetCustInfoItem = (data, option) => {
        if(data.custInfoItem) {
            setCustInfo(data.custInfoItem);
            /* const senders = [...senderList];
            senders.push({name: data.custInfoItem.custName, tel: data.custInfoItem.telNo});
            setSenderList(senders);
            setCallback(data.custInfoItem.telNo); */
        }
    };
    
    const onCompleteGetPhraseList = (data, clientOption) => {
        if(data.smsPhraseList) {
            smsPhraseListRepo(data.smsPhraseList);
        }
    };

    const onCompleteAddPhrase = (data, clientOption) => {
        if(data.smsPhraseAdd.ok) {
            setMessageChanged(false);
            getSmsPhraseList();
        }
        else setResponseAlert({open:true, resultData: data.smsPhraseAdd, title: "자주 쓰는 문구 추가 오류"});
    };

    const onCompleteSendSms = (data, clientOption) => {
        if(data.smsMessageAdd.ok) {
            setMessage('');
            // setReceivers([]);
            // getSmsMessageList();
            const signal = {...smsMessageReloadSignal};
            signal.count += 1;
            smsMessageReloadSignalRepo(signal);
        }
        else {
            setResponseAlert({open:true, resultData: data.smsMessageAdd, title: "SMS 전송 오류"});
        }
    };

    /*
    input InputSmsMessageAdd {
        smsMessage: String!
        receivers: [String!]!
        callBack: String!
    }

    export const ADD_SMS_MESSAGE = gql`
    mutation AddSmsMessage($input: InputSmsMessageAdd!) {
        smsMessageAdd(smsMessage: $input) {
            ok
            message
        }
    }
    `;
    */
    const onClickSend = () => {
        const sender = callback ? callback.replace(/[^0-9]/g, '') : null;
        if(!sender) {
            enqueueSnackbar('발신자(보내는 사람)를 선택하세요.',
                {variant: AppPalette.WarnColor, autoHideDuration: 2500});
            return;
        }
        if(!ValueUtil.isPhoneNumber(sender)) {
            enqueueSnackbar('발신자(보내는 사람) 전화번호 형식이 올바르지 않습니다.',
                {variant: AppPalette.WarnColor, autoHideDuration: 2500});
            return;
        }
        const rcvs
        = receivers.map((r) => {
            if(!r.tel) return null;
            const tel = r.tel.replace(/[^0-9]/g, '');
            if(ValueUtil.isMobileNumber(tel)) return tel;
            return null;
        }).filter(t => t);

        if(rcvs.length === 0) {
            enqueueSnackbar('수신자 번호들이 올바르지 않습니다.',
                {variant: AppPalette.WarnColor, autoHideDuration: 2500});
            return;
        }

        if(!message || message.length === 0) {
            enqueueSnackbar('메시지를 입력하세요.', {variant: AppPalette.WarnColor, autoHideDuration: 2500});
            return;
        }

        sendSmsMessage({variables: {smsMessage: {smsMessage: message, receivers: rcvs, callBack: sender}}});
    };

    const onClickAddAnon = () => {
        if(telAnon && !hasTel(receivers, telAnon)) {
            if(ValueUtil.isMobileNumber(telAnon)) {
                setReceivers([...receivers, {tel: telAnon, name: `(${telAnon})`}]);
                setTelAnon('');
            }
            else {
                enqueueSnackbar('휴대전화번호 형식이 올바르지 않습니다.', {variant: AppPalette.WarnColor, autoHideDuration: 2500});
            }
        }
    };

    const onClickRemoveReceiver = (tel) => {
        setReceivers(receivers.filter(item => item.tel !== tel));
    };

    const onClickSaveSmsPhrase = () => {
        if(message && message.length>0) addSmsPhrase({variables: {smsPhrase:{phrase: message}}});
    };

    const onRequestAddToReceiver = (item) => {
        // check if exists
        if(!hasTel(receivers, item.tel)) {
            setReceivers([...receivers, item]);
        }
    };

    const onRequestAddAllToReceiver = (items) => {
        const newReceivers = items.filter(item => !hasTel(receivers, item.tel));
        setReceivers([...receivers, ...newReceivers]);
    };

    const onRequestUsePhrase = (item) => {
        setMessage(item.phrase);
        setMessageChanged(false);
    };

    const renderByListSelected = () => {
        switch (listNow) {
            case HelperList.SmsPhrases: return <SmsPhrase onRequestUsePhrase={onRequestUsePhrase} />;
            case HelperList.VehicleList:
                return <VehInfoListForSms onRequestAddToReceiver={onRequestAddToReceiver} onRequestAddAllToReceiver={onRequestAddAllToReceiver} />;
            case HelperList.AddressList:
                return <AddressInfoListForSms onRequestAddToReceiver={onRequestAddToReceiver} onRequestAddAllToReceiver={onRequestAddAllToReceiver} />;
            case HelperList.UserList:
                return <UserInfoListForSms onRequestAddToReceiver={onRequestAddToReceiver} onRequestAddAllToReceiver={onRequestAddAllToReceiver} />;
            default: return <Box>준비중입니다. {listNow}</Box>;
        }
    };

    const loadingAny
    = responseCustItem.loading || responseAddPhrase.loading || responsePhraseList.loading || responseSend.loading;

    const renderReceiverItem = (item, idx) => {
        return(
            <ReceiverItem key={item.tel}
                onMouseLeave={() => setHoveredReceiverIndex(null)}
                onMouseEnter={() => setHoveredReceiverIndex(idx)}
            >
                <ReceiverNumber>{idx+1}</ReceiverNumber>
                <ReceiverTel>{item.tel}</ReceiverTel>
                <ReceiverName>{item.name}</ReceiverName>
                <ReceiverControl>
                    {
                        hoveredReceiverIndex === idx && (
                            <IconButton
                                size='small'
                                color='warning'
                                onClick={() => onClickRemoveReceiver(item.tel)}
                            >
                                <PersonRemoveIcon />
                            </IconButton>
                        )
                    }
                </ReceiverControl>
            </ReceiverItem>
        );
    };

    return(
        <SmsMessageContainer>
            <InputBox>
                <InputHeaderBox>
                    <span style={{fontSize: '1.1rem', fontWeight: 'bold',flexGrow:1}}>수신자</span>
                    <Button
                        variant="contained" color="warning"
                        startIcon={<PlaylistRemoveIcon />}
                        onClick={() => setReceivers([])}
                        disabled={receivers.length === 0}
                    >
                        모든 수신자 삭제
                    </Button>
                </InputHeaderBox>
                <ReceiverListBox>
                    <ReceiverList>
                        {
                            receivers.map((item, idx) => (
                                renderReceiverItem(item, idx)
                            ))
                        }
                        {
                            receivers.length === 0 &&
                            <Box style={{paddingTop: 40, textAlign: 'center', color: 'rgba(67, 116, 140, 1)'}}>
                                수신자를 추가하세요
                            </Box>
                        }
                    </ReceiverList>
                </ReceiverListBox>
                <AnonSenderBox>
                    <Box style={{flexGrow:1, paddingRight:10}}>
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="기타 수신자"
                            placeholder="휴대전화번호"
                            style={{backgroundColor:'white'}}
                            onChange={(e) => setTelAnon(e.target.value)}
                            onKeyUp={(e) => {if(e.key==='Enter') onClickAddAnon()}}
                        />
                    </Box>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<PersonAddIcon />}
                        onClick={onClickAddAnon}
                        disabled={!telAnon}
                    >
                        추가
                    </Button>
                </AnonSenderBox>
                <SenderBox>
                    <Tooltip title="화사 전화번호 또는 나의 휴대전화번호만 사용할 수 있습니다.">
                        <Select
                            value={callback || ''}
                            label="발신자"
                            fullWidth
                            onChange={(e) => {setCallback(e.target.value)}}
                        >
                            {
                                senderList.map((sender) => {
                                    return (
                                        <MenuItem
                                            key={sender.tel}
                                            value={sender.tel}
                                        >
                                            {sender.tel} ({sender.name})
                                        </MenuItem>
                                    );
                                })
                            }
                        </Select>
                    </Tooltip>
                </SenderBox>
                <TextBox>
                    {
                        sessionInfo.userAs.smsUseFlag === 'Y'
                        ?
                        <TextField
                            fullWidth
                            variant="outlined"
                            label="전송내용"
                            placeholder="메시지를 입력하세요"
                            multiline
                            rows={3}
                            style={{backgroundColor:'white'}}
                            value={message}
                            onChange={(e) => {setMessage(e.target.value); setMessageChanged(true);}}
                        />
                        :
                        <Box
                            style={{
                                padding: 20, textAlign: 'center', color: AppPalette.PrimaryRGB
                            }}
                        >
                            SMS 문자 전송 계약이 되어 있지 않습니다.<br/>
                            계약을 원하시면 (주)이트레이스로 연락해 주세요.
                        </Box>
                    }
                    <Box style={{display:'flex', padding: '10px 0'}}>
                        <Box style={{flexGrow:1}}>
                            <Button
                                variant="contained"
                                color="inherit"
                                startIcon={<BackupIcon />}
                                fullWidth
                                disabled={!message || message.length===0 || !messageChanged}
                                onClick={onClickSaveSmsPhrase}
                            >자주 쓰는 문구로 저장</Button>
                        </Box>
                        <Box style={{flexGrow:1, paddingLeft: 10}}>
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<SmsIcon />}
                                fullWidth
                                disabled={
                                    !callback ||
                                    !message ||
                                    receivers.length === 0 ||
                                    loadingAny
                                }
                                onClick={onClickSend}
                            >전송</Button>
                        </Box>

                    </Box>
                </TextBox>
            </InputBox>
            <ListsContainer>
                <AppBar
                    position='static' color='default'
                    style={{
                        boxShadow:'none',
                        borderTopLeftRadius: 5, borderTopRightRadius: 5
                    }}
                >
                    <Tabs
                        value={listNow}
                        onChange={(e, newValue) => setListNow(newValue)}
                    >
                        <Tab value={HelperList.VehicleList} label="차량" />
                        <Tab value={HelperList.AddressList} label="주소록" />
                        <Tab value={HelperList.UserList} label="사용자" />
                        <Tab value={HelperList.SmsPhrases} label="자주 쓰는 문구" />
                    </Tabs>
                </AppBar>
                {renderByListSelected()}
            </ListsContainer>
            <ResponseAlert open={responseAlert ? responseAlert.open : false}
                alertData={responseAlert}
                onClose={() => {setResponseAlert(null)}}/>
        </SmsMessageContainer>
    );
}